<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Faqs from "../../../components/faqs.vue";
import myMixin from "../../../plugins/myMixin";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import { required, email, minLength, helpers } from "vuelidate/lib/validators";
import UsersControl from "@/services/westore/entities/users-control";
import FormSkeleton from "../../../components/skeletons/form-skeleton.vue";

const alpha = helpers.regex("alpha", /^[a-zA-Z_ ]*$/);
export default {
  mixins: [myMixin],
  page: {
    title: "Create User",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    Faqs,
    FormSkeleton,
    VueTelInput,
  },
  data() {
    return {
      title: this.$t("Create New User"),
      subtitle: "Please fill all the mandatory fields.",
      submitted: false,
      loader: false,
      showContentLoader: true,
      isSuccess: false,
      successMessage: "",
      faqs: [
        { question: "Question 1", answer: "Answer Answer" },
        {
          question: "How long would it take for the order to be executed?",
          answer:
            "To enable automatic updates for Admin Columns Pro, enter your license key. If you don't have a license key, please see details & pricing.",
        },
      ],
      expandForm: false,
      formInputs: {
        first_name: "",
        email: "",
        password: "",
        phone: "",
        permissions: [],
        status: false,
      },
      permissionsTitle: [
        { name: "Manage Orders" },
        { name: "Manage Returns" },
        { name: "Manage Inbounds" },
        { name: "Manage Integrations" },
        { name: "Manage Inventory" },
      ],
    };
  },
  validations: {
    formInputs: {
      first_name: {
        required,
        alpha,
      },
      email: {
        required,
        email,
      },
      password: { required, minLength: minLength(6) },
      phone: {
        required,
      },
    },
  },
  created() {
    this.init();
  },
  computed: {
    checkLoader() {
      return this.loader;
    },
  },

  methods: {
    init() {
      this.showContentLoader = false;
    },
    submitForm() {
      this.submitted = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loader = true;
        this.logGAEvent("new user", "submit", "submit new user");
      }
      UsersControl.store(this.formInputs)
        .then(() => {
          this.loader = false;
          this.$toast.success("Account has been created successfully");
          setTimeout(
            () =>
              this.$router.push({
                name: "users-control-list",
              }),
            2000
          );
        })
        .catch(({ response }) => {
          this.loader = false;
          this.$toast.error(response.data.message);
        });
    },

    toggleFormListener(val) {
      this.expandForm = val;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :subtitle="subtitle" />
    <div class="row">
      <div
        class="col-md-9 col-sm-12 order-2 order-sm-12"
        :class="expandForm && 'col-md-11 col-sm-12'"
      >
        <div class="card">
          <FormSkeleton v-if="showContentLoader" />
          <div class="card-body" v-else>
            <div class="row">
              <div class="col-12">
                <form class="form-horizontal" @submit.prevent="submitForm">
                  <div
                    class="d-flex flex-column align-items-start justify-content-start mb-5"
                  >
                    <h5 class="text--primary mb-3">
                      {{ $t("User Status") }}
                    </h5>

                    <toggle-button
                      v-model="formInputs.status"
                      :width="100"
                      :height="30"
                      :font-size="13"
                      :color="{ checked: '#43A047', unchecked: '#ea352b' }"
                      :labels="{ checked: 'Active', unchecked: 'Inactive' }"
                      :value="formInputs.status == true ? true : false"
                    />
                  </div>
                  <!-- User Name -->
                  <b-form-group
                    class="mb-3"
                    label-size="lg"
                    :label="$t('User Name *')"
                  >
                    <b-form-input
                      v-model="formInputs.first_name"
                      type="text"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.first_name.$error,
                      }"
                    ></b-form-input>
                    <div
                      v-if="submitted && !$v.formInputs.first_name.alpha"
                      class="invalid-feedback"
                    >
                      <span v-if="!$v.formInputs.first_name.alpha"
                        >Name must be alphabet characters
                      </span>
                    </div>
                  </b-form-group>
                  <!-- Email -->
                  <b-form-group
                    class="mb-3"
                    label-size="lg"
                    :label="$t('Email *')"
                  >
                    <b-form-input
                      v-model="formInputs.email"
                      type="text"
                      :class="{
                        'is-invalid': submitted && $v.formInputs.email.$error,
                      }"
                    ></b-form-input>
                    <div
                      v-if="submitted && !$v.formInputs.email.email"
                      class="invalid-feedback"
                    >
                      <span v-if="!$v.formInputs.email.email"
                        >Enter a valid email</span
                      >
                    </div>
                  </b-form-group>
                  <!-- Password -->
                  <b-form-group
                    class="mb-3"
                    label-size="lg"
                    :label="$t('Password *')"
                  >
                    <b-form-input
                      v-model="formInputs.password"
                      type="password"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.password.$error,
                      }"
                    ></b-form-input>
                    <div
                      v-if="submitted && !$v.formInputs.password.password"
                      class="invalid-feedback"
                    >
                      <span v-if="!$v.formInputs.password.password"
                        >password must be least 6 characters.</span
                      >
                    </div>
                  </b-form-group>
                  <!-- Phone -->
                  <b-form-group
                    class="mb-3"
                    label-size="lg"
                    :label="$t('Phone *')"
                  >
                    <VueTelInput
                      v-model="formInputs.phone"
                      :defaultCountry="'AE'"
                      :preferred-countries="['AE', 'KW', 'SA', 'EG', 'US']"
                      :dropdownOptions="{
                        showDialCodeInSelection: true,
                        showDialCodeInList: true,
                        showFlags: true,
                        showSearchBox: true,
                      }"
                      :inputOptions="{
                        placeholder: 'Phone',
                        maxlength: 15,
                        required: true,
                      }"
                      :invalidMsg="'invalid number'"
                      :validCharactersOnly="true"
                      :mode="'international'"
                    />
                    <span
                      :class="
                        submitted && $v.formInputs.phone.$error
                          ? 'invalid-feedback'
                          : 'text-muted'
                      "
                      >e.g "(xx) xxxx-xxxx"</span
                    >
                  </b-form-group>

                  <div class="row mt-5">
                    <h5 class="text--primary">Permissions</h5>
                    <v-checkbox
                      class="col-md-4 permissions"
                      v-for="(item, i) in permissionsTitle"
                      :key="i"
                      v-model="formInputs.permissions"
                      :value="item.name"
                      color="secondary"
                      :ripple="false"
                      hide-details
                    >
                      <template v-slot:label>
                        <span id="permission-label">{{ item.name }}</span>
                      </template>
                    </v-checkbox>
                  </div>

                  <!-- Submit -->
                  <div class="row float-end">
                    <div class="col-md-2">
                      <v-progress-circular
                        v-if="checkLoader"
                        :size="30"
                        color="secondary"
                        indeterminate
                      ></v-progress-circular>
                      <b-button
                        v-else
                        type="submit"
                        variant="primary"
                        class="btn-block"
                      >
                        {{ $t("Submit") }}
                      </b-button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <!-- end row -->
          </div>
        </div>
        <!-- end card -->
      </div>
      <div
        class="order-1 order-sm-12"
        :class="
          expandForm
            ? 'col-md-1 col-sm-12 d-flex justify-content-end'
            : 'col-md-3 col-sm-12'
        "
      >
        <Faqs :faqs="faqs" title="Users" @toggleForm="toggleFormListener">
          <v-expansion-panels>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                Question 1
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                Answer Answer
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                How long would it take for the order to be executed?
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                To enable automatic updates for Admin Columns Pro, enter your
                license key. If you don't have a license key, please see details
                & pricing.
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </Faqs>
      </div>

      <!-- end col -->
    </div>
    <v-snackbar
      color="success accent-2"
      elavation="24"
      top
      right
      v-model="isSuccess"
    >
      {{ successMessage }}
    </v-snackbar>
  </Layout>
</template>

<style lang="scss">
.permissions,
.switch-user {
  label {
    margin-bottom: 0;
  }
  #permission-label {
    color: #101820;
    font-size: 16px;
    font-weight: 500;
  }
}
.vue-tel-input {
  height: 36px !important;
  color: #495057;
  background-color: #fff !important;
  border: 1px solid #ced4da !important;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  &:focus-within {
    box-shadow: unset !important;
    border: 1px solid #b9bfc4 !important;
  }
  ul::-webkit-scrollbar {
    width: 6px !important;
  }

  ul::-webkit-scrollbar-track {
    background-color: darkgrey !important;
  }

  ul::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3) !important;
  }

  ul {
    overflow-x: hidden !important;
  }
}
.vti__search_box {
  width: 99%;
}
.vti__dropdown-list {
  padding-left: 0 !important;
  width: 56vw !important;
  max-height: 130px !important;
}
.vti__dropdown {
  border-right: 1px solid #bbb;
  &:hover {
    background: transparent !important;
  }
}

@media screen and (max-width: 768px) {
  .vti__dropdown-list {
    width: 78vw !important;
    max-height: 140px !important;
  }
}
</style>
